/**
 * Excel Utilities - Framework-agnostic
 * Core Excel data transformation and processing logic
 * Works with any Excel library (xlsx, exceljs, etc.)
 */

export interface ExcelColumn {
  key: string;
  header: string;
  width?: number;
}

export interface ExcelData {
  headers: string[];
  data: any[];
}

export interface ParsedExcelData {
  headers: string[];
  convertedData: any[];
}

/**
 * Excel Data Processor
 * Framework-agnostic data transformation utilities
 */
export class ExcelDataProcessor {
  /**
   * Transform raw Excel data (array of arrays) to JSON objects
   * @param rawData - Array of arrays from Excel sheet
   * @param options - Processing options
   */
  static parseExcelData(
    rawData: any[][],
    options: {
      skipSerialNumbers?: boolean;
      dateFormat?: string;
    } = {},
  ): ParsedExcelData {
    if (!rawData || rawData.length === 0) {
      return { headers: [], convertedData: [] };
    }

    const { skipSerialNumbers = true } = options;

    // First row is headers
    let headerArray = [...rawData[0]];

    // Convert data rows to objects
    const converted: any[] = [];
    for (let i = 1; i < rawData.length; i++) {
      const subJson = rawData[i];
      const rowData: any = {};

      for (let j = 0; j < subJson.length; j++) {
        const headerKey = headerArray[j];
        rowData[headerKey] = subJson[j] ?? "";

        // Mark as not editable by default
        if (!rowData.hasOwnProperty("isEdit")) {
          rowData.isEdit = false;
        }
      }

      // Remove serial number fields if enabled
      if (skipSerialNumbers) {
        this.removeSerialNumberFields(rowData);
      }

      converted.push(rowData);
    }

    // Clean headers
    if (skipSerialNumbers) {
      headerArray = this.cleanHeaders(headerArray);
    }

    return {
      headers: headerArray,
      convertedData: converted,
    };
  }

  /**
   * Remove serial number fields from headers
   */
  private static cleanHeaders(headers: string[]): string[] {
    const serialNumberPatterns = ["s.no.", "s.no", "no", "no.", "sno", "s no"];

    return headers.filter((header) => {
      if (!header) return true;
      const normalizedHeader = header.toLowerCase().trim();
      return !serialNumberPatterns.includes(normalizedHeader);
    });
  }

  /**
   * Remove serial number fields from data object
   */
  private static removeSerialNumberFields(obj: any): void {
    const serialNumberPatterns = ["s.no.", "s.no", "no", "no.", "sno", "s no"];

    Object.keys(obj).forEach((key) => {
      if (!key) return;
      const normalizedKey = key.toLowerCase().trim();
      if (serialNumberPatterns.includes(normalizedKey)) {
        delete obj[key];
      }
    });
  }

  /**
   * Convert JSON data to Excel-ready array format
   * @param data - Array of objects
   * @param columns - Column definitions (optional)
   */
  static jsonToExcelArray(data: any[], columns?: ExcelColumn[]): any[][] {
    if (!data || data.length === 0) {
      return [];
    }

    let headers: string[];
    let keys: string[];

    if (columns) {
      headers = columns.map((col) => col.header);
      keys = columns.map((col) => col.key);
    } else {
      // Auto-detect from first object
      keys = Object.keys(data[0]);
      headers = keys;
    }

    // Build array with headers first
    const result: any[][] = [headers];

    // Add data rows
    data.forEach((item) => {
      const row = keys.map((key) => item[key] ?? "");
      result.push(row);
    });

    return result;
  }

  /**
   * Validate Excel data structure
   */
  static validateExcelData(
    data: ParsedExcelData,
    requiredHeaders: string[],
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.headers || data.headers.length === 0) {
      errors.push("No headers found in Excel file");
    }

    if (!data.convertedData || data.convertedData.length === 0) {
      errors.push("No data rows found in Excel file");
    }

    // Check required headers
    const missingHeaders = requiredHeaders.filter(
      (required) => !data.headers.includes(required),
    );

    if (missingHeaders.length > 0) {
      errors.push(`Missing required headers: ${missingHeaders.join(", ")}`);
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Transform data for API submission
   */
  static prepareForApi(
    data: ParsedExcelData,
    fieldMapping?: Record<string, string>,
  ): any[] {
    if (!fieldMapping) {
      return data.convertedData;
    }

    // Map Excel column names to API field names
    return data.convertedData.map((row) => {
      const mapped: any = {};
      Object.keys(fieldMapping).forEach((excelKey) => {
        const apiKey = fieldMapping[excelKey];
        if (row.hasOwnProperty(excelKey)) {
          mapped[apiKey] = row[excelKey];
        }
      });
      return mapped;
    });
  }

  /**
   * Filter out empty rows
   */
  static removeEmptyRows(data: any[], keyFields: string[]): any[] {
    return data.filter((row) => {
      return keyFields.some((field) => {
        const value = row[field];
        return value !== null && value !== undefined && value !== "";
      });
    });
  }

  /**
   * Deduplicate data based on key field(s)
   */
  static deduplicate(data: any[], keyFields: string[]): any[] {
    const seen = new Set<string>();
    return data.filter((row) => {
      const key = keyFields.map((field) => row[field]).join("|");
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }
}

/**
 * Excel Template Definitions
 * Column definitions for various Master Data imports
 */
export const ExcelTemplates = {
  Region: {
    requiredHeaders: ["Region Name"],
    columns: [{ key: "region_name", header: "Region Name", width: 30 }],
    fieldMapping: {
      "Region Name": "region_name",
    },
  },

  State: {
    requiredHeaders: ["State Name", "State Code", "Region Name"],
    columns: [
      { key: "state_name", header: "State Name", width: 30 },
      { key: "state_code", header: "State Code", width: 15 },
      { key: "region_name", header: "Region Name", width: 30 },
    ],
    fieldMapping: {
      "State Name": "state_name",
      "State Code": "state_code",
      "Region Name": "region_name",
    },
  },

  City: {
    requiredHeaders: ["City Name", "State Name"],
    columns: [
      { key: "cityname", header: "City Name", width: 30 },
      { key: "state_name", header: "State Name", width: 30 },
    ],
    fieldMapping: {
      "City Name": "cityname",
      "State Name": "state_name",
    },
  },

  BrandCategory: {
    requiredHeaders: ["Category Name", "Short Name"],
    columns: [
      { key: "category_name", header: "Category Name", width: 30 },
      { key: "short_name", header: "Short Name", width: 15 },
    ],
    fieldMapping: {
      "Category Name": "category_name",
      "Short Name": "short_name",
    },
  },

  Brand: {
    requiredHeaders: [
      "Brand Name",
      "Brand Description",
      "Category",
      "SKU",
      "Unit",
      "MRP",
      "Default Points",
      "Unit Per Case",
    ],
    columns: [
      { key: "brand_name", header: "Brand Name", width: 30 },
      { key: "brand_desc", header: "Brand Description", width: 40 },
      { key: "category", header: "Category", width: 20 },
      { key: "sku", header: "SKU", width: 15 },
      { key: "unit", header: "Unit", width: 10 },
      { key: "mrp", header: "MRP", width: 10 },
      { key: "default_points", header: "Default Points", width: 15 },
      { key: "unit_per_case", header: "Unit Per Case", width: 15 },
    ],
    fieldMapping: {
      "Brand Name": "brand_name",
      "Brand Description": "brand_desc",
      Category: "category",
      SKU: "sku",
      Unit: "unit",
      MRP: "mrp",
      "Default Points": "default_points",
      "Unit Per Case": "unit_per_case",
    },
  },

  Language: {
    requiredHeaders: ["Language Name", "Short Name"],
    columns: [
      { key: "lang_name", header: "Language Name", width: 30 },
      { key: "short_name", header: "Short Name", width: 15 },
    ],
    fieldMapping: {
      "Language Name": "lang_name",
      "Short Name": "short_name",
    },
  },

  Unit: {
    requiredHeaders: ["Unit Name", "Unit Description", "Unit Value"],
    columns: [
      { key: "unit_name", header: "Unit Name", width: 20 },
      { key: "unit_desc", header: "Unit Description", width: 40 },
      { key: "unit_value", header: "Unit Value", width: 15 },
    ],
    fieldMapping: {
      "Unit Name": "unit_name",
      "Unit Description": "unit_desc",
      "Unit Value": "unit_value",
    },
  },

  Banner: {
    requiredHeaders: ["Banner Name", "Display Order"],
    columns: [
      { key: "name", header: "Banner Name", width: 30 },
      { key: "display_order", header: "Display Order", width: 15 },
    ],
    fieldMapping: {
      "Banner Name": "name",
      "Display Order": "display_order",
    },
  },
};
